// frontend/src/pages/RoleSelectionPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authAxios from '../auth/authAxios';
import { useMsal } from "@azure/msal-react";

interface RoleDefinition {
  role_id: string;
  role_name: string;
  role_category: string;
  difficulty_level: string;
  description: string;
  available_languages: string[];
  profile_schema: any;
}

interface UserProfile {
  selected_role: string | null;
  role_category: string | null;
  profile_data: any;
  language_preferences: string[];
  years_experience: number | null;
  skill_level: string | null;
  profile_completed_at: string | null;
}

const RoleSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    selected_role: null,
    role_category: null,
    profile_data: null,
    language_preferences: [],
    years_experience: null,
    skill_level: null,
    profile_completed_at: null
  });
  const [selectedRole, setSelectedRole] = useState<RoleDefinition | null>(null);
  const [profileData, setProfileData] = useState<any>({});
  const [currentStep, setCurrentStep] = useState<'role' | 'profile'>('role');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { accounts } = useMsal();
  const user = accounts[0];
  const [hasRoleAssessment, setHasRoleAssessment] = useState(false);
  
  useEffect(() => {
        if (!user) {
          setHasRoleAssessment(false);
          return;
        }
  
        // Check if user has ChatGPT.Admin role (assuming roles in idToken claims)
        const roles = (user.idTokenClaims?.roles || []) as string[];
  
        if (roles.includes("ChatGPT.Assessment")) {
          setHasRoleAssessment(true);
        }
        else {
          setHasRoleAssessment(false);
        }
    }, [user]);

  useEffect(() => {
    fetchRoles();
    fetchUserProfile();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await authAxios.get('/api/roles/definitions');
      setRoles(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load roles');
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await authAxios.get('/api/users/profile');
      const profile = response.data;
      setUserProfile(profile);

      if (profile.selected_role) {
        const role = roles.find(r => r.role_name === profile.selected_role);
        if (role) {
          setSelectedRole(role);
          setProfileData(profile.profile_data || {});
          setCurrentStep('profile');
        }
      }
    } catch (err: any) {
      // User might not have profile yet, which is fine
      console.log('No existing profile found');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role: RoleDefinition) => {
    setSelectedRole(role);
    setProfileData({});
    setCurrentStep('profile');
    setError(null);
  };

  const handleProfileDataChange = (field: string, value: any) => {
    setProfileData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const validateProfile = (): boolean => {
    if (!selectedRole) return false;

    const schema = selectedRole.profile_schema;
    if (!schema) return true; // No validation needed

    // Check required fields
    for (const [fieldName, fieldConfig] of Object.entries(schema)) {
      const config = fieldConfig as any;
      const value = profileData[fieldName];

      if (config.required) {
        if (config.type === 'multi_select') {
          if (!value || !Array.isArray(value) || value.length === 0) {
            setError(`Please select at least one option for ${fieldName.replace(/_/g, ' ')}`);
            return false;
          }
        } else if (config.type === 'skill_matrix' || config.type === 'technical_profile' ||
                   config.type === 'background_profile' || config.type === 'scope_metrics' ||
                   config.type === 'scale_metrics' || config.type === 'data_metrics' ||
                   config.type === 'industry_matrix' || config.type === 'experience_matrix' ||
                   config.type === 'compliance_matrix') {
          // For complex types, check if the object has any values
          if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
            setError(`Please fill in the ${fieldName.replace(/_/g, ' ')} section`);
            return false;
          } else {
            // Check if required sub-fields are filled
            const subFields = config.fields || config.tools || config.platforms || config.metrics || config.areas || config.frameworks || {};
            const hasRequiredValues = Object.keys(subFields).some(subField => {
              const subValue = value[subField];
              return subValue !== undefined && subValue !== null && subValue !== '';
            });
            if (!hasRequiredValues) {
              setError(`Please fill in at least one field in ${fieldName.replace(/_/g, ' ')}`);
              return false;
            }
          }
        } else if (!value || (typeof value === 'string' && value.trim() === '')) {
          setError(`Please fill in the ${fieldName.replace(/_/g, ' ')} field`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }

    // If we're on the role step, just move to profile step
    if (currentStep === 'role') {
      setCurrentStep('profile');
      return;
    }

    // If we're on the profile step, validate and save
    if (!validateProfile()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Determine skill_level based on role category
      let skillLevel = profileData.skill_level;
      if (selectedRole.role_category === 'leadership' && profileData.leadership_level) {
        // Map leadership levels to skill levels
        const leadershipMapping: { [key: string]: string } = {
          "Manager (Team Lead/Senior Manager)": "manager",
          "Director (Director/Senior Director)": "director",
          "VP (Vice President/AVP)": "vp",
          "SVP (Senior Vice President)": "svp"
        };
        skillLevel = leadershipMapping[profileData.leadership_level] || "manager";
      } else if (!skillLevel) {
        // Provide a default skill level for other roles if not specified
        skillLevel = "mid";
      }

      const profilePayload = {
        selected_role: selectedRole.role_name,
        role_category: selectedRole.role_category,
        profile_data: profileData,
        language_preferences: profileData.programming_languages || [],
        years_experience: profileData.years_in_role || profileData.years_in_infrastructure || profileData.years_in_analytics || 0,
        skill_level: skillLevel
      };

      await authAxios.post('/api/users/profile', profilePayload);

      setSuccess('Profile saved successfully!');

      // Redirect to assessments page after a short delay
      setTimeout(() => {
        navigate('/assessments');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to save profile: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      await authAxios.delete('/api/users/profile');
      setSelectedRole(null);
      setProfileData({});
      setCurrentStep('role');
      setUserProfile({
        selected_role: null,
        role_category: null,
        profile_data: null,
        language_preferences: [],
        years_experience: null,
        skill_level: null,
        profile_completed_at: null
      });
      setSuccess('Profile cleared successfully');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear profile');
    }
  };

  const getRoleIcon = (category: string) => {
    switch (category) {
      case 'development': return '💻';
      case 'infrastructure': return '🏗️';
      case 'data': return '📊';
      case 'cybersecurity': return '🔒';
      case 'leadership': return '👥';
      case 'application_admin': return '⚙️';   // Gear icon for Application Administrator
      case 'support_operations': return '🛠️';  // Wrench/tools icon for IT Support & Operations
      case 'quality_assurance': return '✅';   // Checkmark icon for Quality Assurance
      case 'business_analysis': return '💼';   // Keep briefcase for Business Analysis
      default: return '📋';  // Clipboard icon as new default
    }
  };

  const renderProfileForm = () => {
    if (!selectedRole || !selectedRole.profile_schema) return null;

    const schema = selectedRole.profile_schema;

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-white mb-2">Complete Your Profile</h2>
          <p className="text-gray-300">
            Please provide additional information for {selectedRole.role_name}
          </p>
        </div>

        {Object.entries(schema).map(([fieldName, fieldConfig]) => {
          const config = fieldConfig as any;

          if (config.type === 'single_select') {
            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {config.options.map((option: string) => (
                    <button
                      key={option}
                      onClick={() => handleProfileDataChange(fieldName, option)}
                      className={`p-3 rounded-md text-left transition-colors duration-200 ${
                        profileData[fieldName] === option
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          if (config.type === 'multi_select') {
            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.max_selections && ` (Select up to ${config.max_selections})`}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {config.options.map((option: string) => {
                    const isSelected = (profileData[fieldName] || []).includes(option);
                    const currentSelections = profileData[fieldName] || [];
                    const canSelect = !config.max_selections || currentSelections.length < config.max_selections || isSelected;

                    return (
                      <button
                        key={option}
                        disabled={!canSelect}
                        onClick={() => {
                          const current = profileData[fieldName] || [];
                          const updated = isSelected
                            ? current.filter((item: string) => item !== option)
                            : [...current, option];
                          handleProfileDataChange(fieldName, updated);
                        }}
                        className={`p-2 rounded-md text-sm transition-colors duration-200 ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : canSelect
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }

          if (config.type === 'number') {
            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <input
                  type="number"
                  min={config.min || 0}
                  max={config.max || 100}
                  value={profileData[fieldName] || ''}
                  onChange={(e) => handleProfileDataChange(fieldName, parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder={`Enter ${fieldName.replace(/_/g, ' ')}`}
                />
              </div>
            );
          }

          if (config.type === 'text') {
            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <input
                  type="text"
                  value={profileData[fieldName] || ''}
                  onChange={(e) => handleProfileDataChange(fieldName, e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder={config.placeholder || `Enter ${fieldName.replace(/_/g, ' ')}`}
                />
              </div>
            );
          }

          if (config.type === 'boolean') {
            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={profileData[fieldName] || false}
                    onChange={(e) => handleProfileDataChange(fieldName, e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-white font-medium">
                    {config.label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    {config.required && <span className="text-red-400 ml-1">*</span>}
                  </span>
                </label>
              </div>
            );
          }

          // Handle complex field types by flattening them into simple fields
          if (config.type === 'skill_matrix') {
            const currentData = profileData[fieldName] || {};
            const tools = config.tools || config.platforms || {};

            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="space-y-3">
                  {Object.entries(tools).map(([tool, levels]) => (
                    <div key={tool} className="flex items-center space-x-3">
                      <span className="text-gray-300 w-32 text-sm">{tool}:</span>
                      <select
                        value={currentData[tool] || (Array.isArray(levels) ? levels[0] : '')}
                        onChange={(e) => {
                          const updated = { ...currentData, [tool]: e.target.value };
                          handleProfileDataChange(fieldName, updated);
                        }}
                        className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                      >
                        {Array.isArray(levels) ? levels.map((level: string) => (
                          <option key={level} value={level}>{level}</option>
                        )) : (
                          <option value="">Select...</option>
                        )}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (config.type === 'technical_profile' || config.type === 'background_profile') {
            const currentData = profileData[fieldName] || {};
            const fields = config.fields || {};

            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="space-y-3">
                  {Object.entries(fields).map(([subField, subConfig]: [string, any]) => (
                    <div key={subField}>
                      <label className="block text-gray-300 text-sm mb-1">
                        {subConfig.label || subField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                      {subConfig.type === 'number' ? (
                        <input
                          type="number"
                          min={subConfig.min || 0}
                          max={subConfig.max || 100}
                          value={currentData[subField] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [subField]: parseInt(e.target.value) || 0 };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        />
                      ) : subConfig.type === 'single_select' ? (
                        <select
                          value={currentData[subField] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [subField]: e.target.value };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Select...</option>
                          {subConfig.options?.map((option: string) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : subConfig.type === 'boolean' ? (
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={currentData[subField] || false}
                            onChange={(e) => {
                              const updated = { ...currentData, [subField]: e.target.checked };
                              handleProfileDataChange(fieldName, updated);
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-gray-300 text-sm">Yes</span>
                        </label>
                      ) : (
                        <input
                          type="text"
                          value={currentData[subField] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [subField]: e.target.value };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          placeholder={subConfig.placeholder || ''}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // Handle other complex types by treating them as grouped fields
          if (config.type === 'scope_metrics' || config.type === 'scale_metrics' || config.type === 'data_metrics') {
            const currentData = profileData[fieldName] || {};
            const metrics = config.metrics || {};

            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="space-y-3">
                  {Object.entries(metrics).map(([metric, metricConfig]: [string, any]) => (
                    <div key={metric}>
                      <label className="block text-gray-300 text-sm mb-1">
                        {metricConfig.label || metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                      {metricConfig.type === 'number' ? (
                        <input
                          type="number"
                          min={metricConfig.min || 0}
                          max={metricConfig.max || 999999}
                          value={currentData[metric] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [metric]: parseInt(e.target.value) || 0 };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        />
                      ) : metricConfig.type === 'currency' ? (
                        <input
                          type="text"
                          value={currentData[metric] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [metric]: e.target.value };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          placeholder="e.g., $1M, $500K"
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        />
                      ) : metricConfig.type === 'multi_select' ? (
                        <div className="grid grid-cols-2 gap-2">
                          {metricConfig.options?.map((option: string) => {
                            const isSelected = (currentData[metric] || []).includes(option);
                            return (
                              <button
                                key={option}
                                onClick={() => {
                                  const current = currentData[metric] || [];
                                  const updated = isSelected
                                    ? current.filter((item: string) => item !== option)
                                    : [...current, option];
                                  const newData = { ...currentData, [metric]: updated };
                                  handleProfileDataChange(fieldName, newData);
                                }}
                                className={`p-2 rounded-md text-sm transition-colors duration-200 ${
                                  isSelected
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                              >
                                {option}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={currentData[metric] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [metric]: e.target.value };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // Handle matrix types (industry_matrix, experience_matrix, compliance_matrix)
          if (config.type === 'industry_matrix' || config.type === 'experience_matrix' || config.type === 'compliance_matrix') {
            const currentData = profileData[fieldName] || {};
            const areas = config.areas || config.frameworks || {};

            return (
              <div key={fieldName} className="bg-gray-800 rounded-lg p-4">
                <label className="block text-white font-medium mb-3">
                  {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {config.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="space-y-3">
                  {Object.entries(areas).map(([area, areaConfig]: [string, any]) => (
                    <div key={area}>
                      <label className="block text-gray-300 text-sm mb-1">
                        {areaConfig.label || area.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                      {areaConfig.type === 'boolean' ? (
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={currentData[area] || false}
                            onChange={(e) => {
                              const updated = { ...currentData, [area]: e.target.checked };
                              handleProfileDataChange(fieldName, updated);
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-gray-300 text-sm">Yes</span>
                        </label>
                      ) : areaConfig.type === 'single_select' ? (
                        <select
                          value={currentData[area] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [area]: e.target.value };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Select...</option>
                          {areaConfig.options?.map((option: string) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={currentData[area] || ''}
                          onChange={(e) => {
                            const updated = { ...currentData, [area]: e.target.value };
                            handleProfileDataChange(fieldName, updated);
                          }}
                          placeholder={areaConfig.placeholder || ''}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:border-blue-500 focus:outline-none"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };

  if (user && !hasRoleAssessment) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">You do not have access to this page.</div>
        </div>
      </div>
    );
  } 

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {currentStep === 'role' ? 'Select Your IT Area' : 'Complete Your Profile'}
        </h1>
        <p className="text-gray-300">
          {currentStep === 'role'
            ? 'Choose your primary IT area to get personalized assessments.'
            : 'Provide additional details to customize your assessment experience.'
          }
        </p>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500 rounded-md p-4 mb-6">
          <div className="text-red-400">{error}</div>
        </div>
      )}

      {success && (
        <div className="bg-green-900/20 border border-green-500 rounded-md p-4 mb-6">
          <div className="text-green-400">{success}</div>
        </div>
      )}

      {userProfile.selected_role && (
        <div className="bg-blue-900/20 border border-blue-500 rounded-md p-4 mb-6">
          <div className="text-blue-400">
            <strong>Current Role:</strong> {userProfile.selected_role}
            {userProfile.profile_completed_at && (
              <span className="text-sm text-blue-300 ml-2">
                (Completed on {new Date(userProfile.profile_completed_at).toLocaleDateString()})
              </span>
            )}
          </div>
        </div>
      )}

      {currentStep === 'role' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {roles.map((role) => (
            <div
              key={role.role_id}
              className={`border-2 rounded-lg p-6 cursor-pointer transition-all duration-200 ${
                selectedRole?.role_id === role.role_id
                  ? 'border-blue-500 bg-blue-900/20'
                  : 'border-gray-600 bg-gray-800 hover:border-gray-500'
              }`}
              onClick={() => handleRoleSelect(role)}
            >
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">{getRoleIcon(role.role_category)}</span>
                <h3 className="text-xl font-semibold text-white">
                  {role.role_name}
                </h3>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                {role.description}
              </p>
              <div className="text-xs text-gray-400">
                Category: {role.role_category}
              </div>
            </div>
          ))}
        </div>
      ) : (
        renderProfileForm()
      )}

      <div className="mt-8 flex space-x-4">
        {currentStep === 'profile' && (
          <button
            onClick={() => setCurrentStep('role')}
            className="bg-gray-600 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium transition-colors duration-200"
          >
            Back to Role Selection
          </button>
        )}

        <button
          onClick={handleSave}
          disabled={saving || !selectedRole}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-md font-medium transition-colors duration-200"
        >
          {saving ? 'Saving...' : currentStep === 'role' ? 'Continue to Profile' : 'Save Profile'}
        </button>

        {userProfile.selected_role && (
          <button
            onClick={handleClear}
            className="bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-md font-medium transition-colors duration-200"
          >
            Clear Profile
          </button>
        )}
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={() => navigate('/assessments')}
          className="text-blue-400 hover:text-blue-300 underline"
        >
          Skip to Assessments
        </button>
      </div>
    </div>
  );
};

export default RoleSelectionPage;
